import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { NAV_LINKS } from "../mock/mock";
import { useCart } from "../context/CartContext";
import Logo from "./Logo";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "./ui/sheet";

export default function Navbar() {
  const { count, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const signIn = () =>
    toast("Sign in", { description: "Accounts are coming soon in this demo." });

  return (
    <header
      className={`fixed top-9 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-md border-b border-white/10"
          : "bg-gradient-to-b from-black/80 to-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Logo />

        <ul className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.map((l) => (
            <li key={l.label}>
              <Link
                to={l.to}
                className={`text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                  l.accent
                    ? "text-trash hover:text-[#ff7a60]"
                    : "text-white/80 hover:text-neon"
                }`}
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3 md:gap-5">
          <button
            onClick={signIn}
            className="hidden sm:flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-white/80 hover:text-neon transition-colors"
          >
            <User size={17} /> Sign In
          </button>

          <button
            onClick={() => setIsOpen(true)}
            className="relative text-white hover:text-neon transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-neon text-black text-[10px] font-bold h-4 min-w-4 px-1 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden text-white hover:text-neon" aria-label="Menu">
                <Menu size={24} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-black border-white/10 w-[300px] p-0">
              <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
                <Logo className="text-lg" />
                <SheetClose asChild>
                  <button className="text-white/70 hover:text-white"><X size={22} /></button>
                </SheetClose>
              </div>
              <ul className="flex flex-col p-3">
                {NAV_LINKS.map((l) => (
                  <li key={l.label}>
                    <SheetClose asChild>
                      <Link
                        to={l.to}
                        className={`block px-4 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] rounded-md hover:bg-white/5 ${
                          l.accent ? "text-trash" : "text-white/85"
                        }`}
                      >
                        {l.label}
                      </Link>
                    </SheetClose>
                  </li>
                ))}
                <li>
                  <button
                    onClick={signIn}
                    className="w-full text-left px-4 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] rounded-md hover:bg-white/5 text-white/85"
                  >
                    Sign In
                  </button>
                </li>
              </ul>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
